<template>
  <section class="rounded-[24px] border bg-white p-4 shadow-sm sm:p-5">
    <div class="text-center">
      <div class="flex items-center justify-center gap-4">
        <i class="ri-leaf-line -rotate-45 text-5xl leading-none text-[#2f2f2f]"></i>
        <p class="text-[4rem] font-semibold leading-none text-[#222] sm:text-[5rem]">{{ summary.rating }}</p>
        <i class="ri-leaf-line rotate-45 text-5xl leading-none text-[#2f2f2f]"></i>
      </div>
      <h2 class="mt-4 text-xl font-semibold text-[#222]">{{ summary.title }}</h2>
      <p class="mx-auto mt-2 max-w-md text-sm leading-6 text-[#666] sm:text-base">{{ summary.description }}</p>
      <button type="button" class="mt-1 text-sm font-medium text-[#666] underline underline-offset-2">
        How reviews work
      </button>
    </div>

    <div class="mt-8 grid gap-4 border-b pb-7 md:grid-cols-3 xl:grid-cols-7">
      <div class="md:col-span-3 xl:col-span-1">
        <p class="text-sm font-semibold text-[#222]">Overall rating</p>
        <div class="mt-2 space-y-1.5">
          <div v-for="item in overallBars" :key="item.score" class="flex items-center gap-2 text-xs text-[#333]">
            <span class="w-3">{{ item.score }}</span>
            <span class="h-1 w-full rounded-full bg-[#d9d9d9]">
              <span class="block h-1 rounded-full bg-[#222]" :style="{ width: item.width }"></span>
            </span>
          </div>
        </div>
      </div>

      <article v-for="item in ratings" :key="item.label" class="border-t pt-4 md:border-l md:border-t-0 md:pl-5">
        <p class="text-sm font-semibold text-[#222]">{{ item.label }}</p>
        <p class="mt-1 text-xl font-semibold text-[#222]">{{ item.score }}</p>
        <i :class="[item.icon, 'mt-6 block text-3xl text-[#222]']"></i>
      </article>
    </div>

    <div class="mt-5 flex gap-2 overflow-x-auto pb-2">
      <span v-for="tag in tags" :key="tag.label" class="inline-flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold text-[#222]">
        <i :class="[tag.icon, 'text-base text-[#222]']"></i>
        <span>{{ tag.label }}</span>
        <span class="font-normal text-[#777]">{{ tag.count }}</span>
      </span>
    </div>

    <div class="mt-7 grid gap-x-12 gap-y-8 lg:grid-cols-2">
      <article v-for="review in previewReviews" :key="review.id">
        <div class="flex items-center gap-3">
          <img :src="review.avatar" :alt="review.name" class="h-12 w-12 rounded-full object-cover">
          <div>
            <h3 class="font-semibold text-[#222]">{{ review.name }}</h3>
            <p class="text-sm text-[#666]">{{ review.meta }}</p>
          </div>
        </div>
        <p class="mt-4 text-sm font-semibold tracking-[0.08em] text-[#222]">★★★★★ <span class="font-normal tracking-normal text-[#666]">· {{ review.date }}</span></p>
        <p class="mt-2 line-clamp-3 text-[1rem] leading-6 text-[#222]">{{ review.text }}</p>
        <button type="button" class="mt-2 text-sm font-semibold text-[#222] underline underline-offset-2" @click="openReviewsModal">
          Show more
        </button>
      </article>
    </div>

    <button
      type="button"
      class="mt-8 rounded-xl bg-[#f1f1f1] px-6 py-3 text-sm font-semibold text-[#222] transition hover:bg-[#e7e7e7]"
      @click="openReviewsModal"
    >
      Show all {{ reviewCount }} reviews
    </button>

    <Teleport to="body">
      <div v-if="reviewsModalOpen" class="fixed inset-0 z-[9999] bg-black/50 px-3 py-6" @click.self="closeReviewsModal">
        <section class="mx-auto flex max-h-[92vh] max-w-4xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
          <div class="flex items-center justify-between border-b px-5 py-4">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#777]">Guest reviews</p>
              <h2 class="text-xl font-semibold text-[#222]">All {{ reviewCount }} reviews</h2>
            </div>
            <button type="button" class="grid h-10 w-10 place-items-center rounded-full border text-xl text-[#222] transition hover:bg-[#f4f4f4]" aria-label="Close reviews" @click="closeReviewsModal">
              <i class="ri-close-line"></i>
            </button>
          </div>

          <div class="overflow-y-auto px-5 py-5">
            <div class="grid gap-x-10 gap-y-8 md:grid-cols-2">
              <article v-for="review in reviews" :key="`modal-${review.id}`">
                <div class="flex items-center gap-3">
                  <img :src="review.avatar" :alt="review.name" class="h-12 w-12 rounded-full object-cover">
                  <div>
                    <h3 class="font-semibold text-[#222]">{{ review.name }}</h3>
                    <p class="text-sm text-[#666]">{{ review.meta }}</p>
                  </div>
                </div>
                <p class="mt-4 text-sm font-semibold tracking-[0.08em] text-[#222]">★★★★★ <span class="font-normal tracking-normal text-[#666]">· {{ review.date }}</span></p>
                <p class="mt-2 text-[0.95rem] leading-6 text-[#222]">{{ review.text }}</p>
              </article>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import venueReviews from '@/data/venue-reviews.json'

const reviewsModalOpen = ref(false)

const summary = computed(() => venueReviews.summary || {})
const ratings = computed(() => venueReviews.ratings || [])
const tags = computed(() => venueReviews.tags || [])
const reviews = computed(() => venueReviews.reviews || [])
const reviewCount = computed(() => Number(summary.value.reviewCount || reviews.value.length || 0))
const previewReviews = computed(() => reviews.value.slice(0, 6))
const overallBars = computed(() => [
  { score: 5, width: '100%' },
  { score: 4, width: '8%' },
  { score: 3, width: '4%' },
  { score: 2, width: '2%' },
  { score: 1, width: '1%' }
])

const openReviewsModal = () => {
  reviewsModalOpen.value = true
}

const closeReviewsModal = () => {
  reviewsModalOpen.value = false
}
</script>
